import { useEffect, useRef } from 'react'
import { useCurrentUserStore } from '@store'
import { useShareMyLocation } from './useShareMyLocation'
import { useGetNearbyVisibility } from './api/queries/useGetNearbyVisibility'

export const useTrackLocation = (isLoggedIn: boolean = false) => {
  const shareMyLocation = useShareMyLocation()
  const { userID } = useCurrentUserStore()
  // Only query visibility once logged in: this hook is mounted globally (WebLayout),
  // and an unauthenticated call 401s → interceptor signs out → login page redirects.
  const {
    data: visibilityData,
    isLoading: isVisibilityLoading,
    isError: isVisibilityError,
  } = useGetNearbyVisibility(isLoggedIn)
  const tracked = useRef(false)

  useEffect(() => {
    // Wait until we know the visibility scope before doing anything, so we never
    // publish coordinates before confirming the user has not opted out. The
    // account id (restored from storage) says whose daily save it is.
    if (!isLoggedIn || !userID || tracked.current || isVisibilityLoading) return

    // Visibility unknown (the read failed): they may have chosen to be
    // invisible, so nothing is published; a later successful read still can.
    if (isVisibilityError) return

    // Privacy: if the user chose to stay invisible (HIDDEN = "not visible to
    // anyone"), never collect or publish their location.
    if (visibilityData?.scope === 'HIDDEN') {
      tracked.current = true
      return
    }

    tracked.current = true
    // At most once a day (useShareMyLocation).
    void shareMyLocation(userID)
  }, [isLoggedIn, shareMyLocation, userID, isVisibilityLoading, isVisibilityError, visibilityData?.scope])
}
